import { format, isValid } from "date-fns";

import { AssignedTag } from "services/types/assigned-tag.type";

/** What a tag with no colour of its own is drawn in. */
export const DEFAULT_TAG_COLOR = "#6A6A7D";

/**
 * A tag's colour, guaranteed to be something MUI can work with.
 *
 * `palette.getContrastText` throws on `undefined`, on `null` AND on `""` — the last one
 * with "MUI: Unsupported `` color" — so a single colourless tag does not render grey, it
 * takes down whatever is drawing it. Tag colour is nullable at the source and every
 * consumer of this folder inherits the crash, so the guard belongs here rather than at
 * each of the ~60 call sites.
 *
 * Measured rather than assumed, against `createTheme().palette.getContrastText`:
 *   undefined -> throws (reading 'type')
 *   null      -> throws (reading 'type')
 *   ""        -> throws (Unsupported `` color)
 */
export const tagColor = (color?: string | null): string =>
  color && color.trim() ? color : DEFAULT_TAG_COLOR;

export const getAssignedTagTooltip = (
  assignedTag: AssignedTag,
  isTagExpired: boolean
) => {
  const { tag, description, expires_at } = assignedTag;
  const parsedDate = expires_at ? new Date(expires_at) : null;
  const formattedExpiredDate =
    parsedDate && isValid(parsedDate) ? format(parsedDate, "MM/dd/yyyy") : "";

  const parts: string[] = [];

  if (description) {
    parts.push(description);
  }

  if (tag.tooltip) {
    const tooltipText = description ? `(${tag.tooltip})` : tag.tooltip;
    parts.push(tooltipText);
  }

  if (expires_at && formattedExpiredDate) {
    const expiredText = isTagExpired
      ? `expired on ${formattedExpiredDate}`
      : `until ${formattedExpiredDate}`;
    const prefix = parts.length > 0 ? "- " : "";
    parts.push(`${prefix}${expiredText}`);
  }

  return parts.join(" ");
};
